import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material'
import { SimpleSelectableCard } from 'Components/Cards/CaregiverPortal/SimpleSelectableCard'
import PersonIcon from '@mui/icons-material/Person'
import HomeIcon from '@mui/icons-material/Home'
import { useEffect, useState } from 'react'
import { generatePath, useLocation, useNavigate, useParams } from 'react-router'
import { useAuth } from 'hooks/use-auth'
import { CAREGIVER_PORTAL, CAREGIVER_PORTAL_DASHBOARD } from 'Pages/Routes'
import { deleteSession, saveSession, useCaregiverWizardContext } from 'ContextProviders/CaregiverWizard'
import { type EnrollmentDetails } from 'core/types'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { CareManagementSummary } from 'Components/CareManagement/CareManagementSummary'

interface CareMgmtData {
  childName: string
  childId: number
}

export function ShortFormSubmitted (): JSX.Element {
  const location = useLocation()
  const careManagements: CareMgmtData[] = location.state?.careManagements?.careManagementAssignments
  let firstName: string = ''
  let lastName: string = ''
  if (location.state != null) {
    firstName = location.state.firstName
    lastName = location.state.lastName
  }

  const { isdId, isdServiceProviderId } = useParams()
  const nav = useNavigate()
  const { user, createAccount } = useAuth()
  const [continueToCreateAccount, setContinueToCreateAccount] = useState<boolean>(true)
  const caregiverForm = useCaregiverWizardContext()
  const [loaded, setLoaded] = useState(false)
  const handleContinue = async (): Promise<void> => {
    if (continueToCreateAccount) {
      const emptyForm: EnrollmentDetails = {
        unbornChild: false,
        isCaregiverOfChild: false,
        userServiceProviderId: null,
        hasStartedCreatingAccount: true,
        currentStep: 0,
        currentSubStep: 0,
        note: '',
        currentChild: {
          firstName: null,
          lastName: '',
          dateOfBirth: null,
          languageID: null,
          isHomeless: false,
          unborn: false,
          isCurrentlyUpdating: false,
          hasPendingDuplicate: false,
          imageKey: null,
          location: {
            address: '',
            apartment: '',
            city: '',
            state: '',
            zipCode: ''
          }
        },
        currentCaregiver: {
          firstName,
          lastName,
          primaryPhone: '',
          secondaryPhone: '',
          email: '',
          doesNotHaveEmail: false,
          preferredContactMethod: null,
          isLivingWithStudent: false,
          location: {
            address: '',
            apartment: '',
            city: '',
            state: '',
            zipCode: ''
          },
          isCurrentlyUpdating: false
        },
        children: [],
        caregivers: [],
        isNewSession: false
      }
      await saveSession(emptyForm)
      createAccount()
    } else {
      handleBack()
    }
  }
  const [canShowCareManagement, setCanShowCareManagement] = useState(false)
  useEffect(() => {
    if (caregiverForm?.hasStartedCreatingAccount === true && user != null && !loaded) {
      setLoaded(true)
      void handleAccountCreation()
    }
    void loadCareMgmt()
  }, [user, caregiverForm])
  const loadCareMgmt = async (): Promise<void> => {
    const { response: isdRsp } = await sendGet(`/CaregiverPortal/CanShowCareManagement?isdid=${isdId ?? ''}`, {})
    if (isdRsp != null) {
      setCanShowCareManagement(isdRsp)
    }
  }
  const handleAccountCreation = async (): Promise<void> => {
    if (user == null) {
      return
    }
    const usrData = {
      firstName: caregiverForm?.currentCaregiver.firstName,
      lastName: caregiverForm?.currentCaregiver.lastName,
      email: user.email,
      externalLogin: user.externalLogin,
      linkCaregiver: true
    }
    // Update the user in SSO with the first and last name, then create the MIECC user
    await sendPost('/Account/UpdateAccount', usrData)
    await sendPost('/CaregiverPortal/CreateUser', usrData)
    deleteSession()
    const homeRoute = generatePath(CAREGIVER_PORTAL_DASHBOARD)
    nav(homeRoute)
    window.location.reload()
  }

  const handleBack = (): void => {
    const homeRoute = generatePath(CAREGIVER_PORTAL, { isdId: isdId ?? '', isdServiceProviderId: isdServiceProviderId ?? '' })
    nav(homeRoute)
  }

  return <Container>
    <Grid container>
      <Grid item xs={12} sx={{ my: 1 }}>
        <Typography variant='h4'>Thank you! Your interest has been submitted</Typography>
        <Typography variant='body1'>Your interest form has been successfully submitted. It will be evaluated by a coordinator and they will reach out to collect additional information.</Typography>
      </Grid>
      {canShowCareManagement && careManagements?.length > 0 && (<Box>
        <Box>Care Managements Added</Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <CareManagementSummary childID={careManagements[0].childId} childName={careManagements[0].childName} />
        </Box>
      </Box>)}
      <Grid item xs={12} sx={{ my: 1 }}>
        <Typography variant='h6'>What do I do next?</Typography>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Stack
          direction='column'
          spacing={2}
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch'
          }}
        >
          <SimpleSelectableCard
            header='Create an account'
            description='While you wait to hear back, create a profile so that you can get interactive updates on services for your child.'
            imageElement={<PersonIcon className='card-icon' />}
            selected={continueToCreateAccount}
            onClick={() => { setContinueToCreateAccount(true) }}
          />
          <SimpleSelectableCard
            header={'No, I\'m done'}
            description='Having an up-to-date profile can help us identify missing services or resources that you may find helpful in the future.'
            imageElement={<HomeIcon className='card-icon' />}
            selected={!continueToCreateAccount}
            onClick={() => { setContinueToCreateAccount(false) }}
          />
        </Stack>
      </Grid>
      <Grid container item xs={12} spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={6}>
          <Button
            className='back-button'
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            className='footer-button'
            onClick={handleContinue}
            variant="contained"
          >
            Continue
          </Button>
        </Grid>
      </Grid>
    </Grid>
  </Container>
}
